'use client'

import React from 'react'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { useBirhaus } from '@birhaus/provider'
import { 
  PieChart,
  AlertTriangle,
  CheckCircle,
  Clock,
  Plus,
  Edit,
  Trash2,
  Car,
  Home,
  ShoppingCart,
  Utensils,
  Plane,
  Heart,
  BookOpen,
  Smartphone,
  Calculator
} from 'lucide-react'
import { formatCurrency } from '../utils/currency'
import { BirhausCurrencyDisplay } from './BirhausCurrencyDisplay'
import type { 
  BirhausBudgetTrackerProps, 
  Budget,
  BudgetCategory,
  BudgetStatus,
  BudgetPeriod 
} from '../types/financial'

// Local cn utility
const cn = (...inputs: any[]) => twMerge(clsx(inputs))

// Budget category configuration
const budgetCategoryConfig: Record<BudgetCategory, {
  icon: React.ReactNode
  colorClass: string
  nameEs: string
  nameEn: string
}> = {
  alimentacion: {
    icon: <Utensils size={20} />,
    colorClass: 'text-green-600 bg-green-100',
    nameEs: 'Alimentación',
    nameEn: 'Food & Dining'
  },
  transporte: {
    icon: <Car size={20} />,
    colorClass: 'text-blue-600 bg-blue-100',
    nameEs: 'Transporte',
    nameEn: 'Transportation'
  },
  vivienda: {
    icon: <Home size={20} />,
    colorClass: 'text-purple-600 bg-purple-100',
    nameEs: 'Vivienda',
    nameEn: 'Housing'
  },
  entretenimiento: {
    icon: <Plane size={20} />,
    colorClass: 'text-orange-600 bg-orange-100',
    nameEs: 'Entretenimiento',
    nameEn: 'Entertainment'
  },
  salud: {
    icon: <Heart size={20} />,
    colorClass: 'text-red-600 bg-red-100',
    nameEs: 'Salud',
    nameEn: 'Healthcare'
  },
  educacion: {
    icon: <BookOpen size={20} />,
    colorClass: 'text-indigo-600 bg-indigo-100',
    nameEs: 'Educación',
    nameEn: 'Education'
  },
  compras: {
    icon: <ShoppingCart size={20} />,
    colorClass: 'text-pink-600 bg-pink-100',
    nameEs: 'Compras',
    nameEn: 'Shopping'
  },
  tecnologia: {
    icon: <Smartphone size={20} />,
    colorClass: 'text-gray-600 bg-gray-100',
    nameEs: 'Tecnología',
    nameEn: 'Technology'
  },
  servicios: {
    icon: <Calculator size={20} />,
    colorClass: 'text-indigo-600 bg-indigo-100',
    nameEs: 'Servicios',
    nameEn: 'Services'
  },
  otros: {
    icon: <Calculator size={20} />,
    colorClass: 'text-yellow-600 bg-yellow-100',
    nameEs: 'Otros',
    nameEn: 'Other'
  }
}

// Budget status configuration
const budgetStatusConfig: Record<BudgetStatus, {
  colorClass: string
  nameEs: string
  nameEn: string
  icon: React.ReactNode
}> = {
  activo: {
    colorClass: 'text-blue-700 bg-blue-100',
    nameEs: 'Activo',
    nameEn: 'Active',
    icon: <CheckCircle size={16} />
  },
  pausado: {
    colorClass: 'text-gray-700 bg-gray-100',
    nameEs: 'Pausado',
    nameEn: 'Paused',
    icon: <Clock size={16} />
  },
  completado: {
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'Completado',
    nameEn: 'Completed',
    icon: <CheckCircle size={16} />
  },
  excedido: {
    colorClass: 'text-red-700 bg-red-100',
    nameEs: 'Excedido',
    nameEn: 'Over Budget',
    icon: <AlertTriangle size={16} />
  },
  cerca_limite: {
    colorClass: 'text-yellow-700 bg-yellow-100',
    nameEs: 'Cerca del Límite',
    nameEn: 'Near Limit',
    icon: <Clock size={16} />
  },
  en_objetivo: {
    colorClass: 'text-green-700 bg-green-100',
    nameEs: 'En Objetivo',
    nameEn: 'On Track',
    icon: <CheckCircle size={16} />
  }
}

/**
 * BirhausBudgetTracker - Budget management and tracking component
 * 
 * Features:
 * - Multiple budget categories with appropriate icons and colors
 * - Real-time spending tracking with progress indicators
 * - Budget alerts and warnings when approaching/exceeding limits
 * - Period-based budget management (monthly, weekly, yearly)
 * - Spanish-first category names and descriptions
 * - Miller's Law compliance (max 7 visible categories)
 * - CRUD operations for budget management
 * - Accessibility compliant with proper ARIA labels
 * - Responsive layout for different screen sizes
 * - Summary statistics and recommendations
 */
export function BirhausBudgetTracker({
  budgets,
  periodo = 'mensual',
  moneda = 'PYG',
  showAddButton = true,
  showEditButtons = true,
  onAddBudget,
  onEditBudget,
  onDeleteBudget,
  className,
  ...props
}: BirhausBudgetTrackerProps) {
  const { language, reportViolation } = useBirhaus()

  // BIRHAUS validation: Check cognitive load (max 7 budgets - Miller's Law)
  React.useEffect(() => {
    if (budgets.length > 7) {
      reportViolation({
        type: 'cognitive',
        severity: 'warning',
        element: document.createElement('div'),
        message: `Budget tracker shows ${budgets.length} categories (max recommended: 7)`,
        messageEs: `Seguidor de presupuesto muestra ${budgets.length} categorías (máximo recomendado: 7)`,
        messageEn: `Budget tracker shows ${budgets.length} categories (max recommended: 7)`,
        recommendation: 'Consider grouping related categories or using tabs for large numbers',
        birhausPrinciple: 1 // Cognitive load reduction
      })
    }
  }, [budgets.length, reportViolation])

  // Calculate total budget and spending
  const totals = React.useMemo(() => {
    const totalBudget = budgets.reduce((sum, budget) => sum + (budget.limite ?? budget.montoPresupuesto), 0)
    const totalSpent = budgets.reduce((sum, budget) => sum + (budget.gastado ?? budget.montoGastado), 0)
    const totalRemaining = totalBudget - totalSpent
    const spentPercentage = totalBudget > 0 ? (totalSpent / totalBudget) * 100 : 0

    return {
      totalBudget,
      totalSpent,
      totalRemaining,
      spentPercentage
    }
  }, [budgets])

  // Get budget status
  const getBudgetStatus = React.useCallback((budget: Budget): BudgetStatus => {
    const spent = budget.gastado ?? budget.montoGastado
    const limit = budget.limite ?? budget.montoPresupuesto
    const percentage = limit > 0 ? (spent / limit) * 100 : 0
    
    if (percentage >= 100) return 'excedido'
    if (percentage >= 80) return 'cerca_limite'
    return 'en_objetivo'
  }, [])

  // Calculate progress percentage
  const getProgressPercentage = React.useCallback((budget: Budget): number => {
    const spent = budget.gastado ?? budget.montoGastado
    const limit = budget.limite ?? budget.montoPresupuesto
    return Math.min(100, limit > 0 ? (spent / limit) * 100 : 0)
  }, [])

  // Handle budget actions
  const handleAddBudget = React.useCallback(() => {
    onAddBudget?.()
  }, [onAddBudget])

  const handleEditBudget = React.useCallback((budget: Budget) => {
    onEditBudget?.(budget)
  }, [onEditBudget])

  const handleDeleteBudget = React.useCallback((budget: Budget) => {
    onDeleteBudget?.(budget)
  }, [onDeleteBudget])

  // Get period name
  const getPeriodName = React.useCallback((period: BudgetPeriod): string => {
    const periods = {
      semanal: { es: 'Semanal', en: 'Weekly' },
      mensual: { es: 'Mensual', en: 'Monthly' },
      anual: { es: 'Anual', en: 'Annual' }
    }
    return language === 'es' ? periods[period].es : periods[period].en
  }, [language])

  // Sort budgets by spending percentage (highest first for attention)
  const sortedBudgets = React.useMemo(() => {
    return [...budgets].sort((a, b) => {
      const aPercentage = getProgressPercentage(a)
      const bPercentage = getProgressPercentage(b)
      return bPercentage - aPercentage
    })
  }, [budgets, getProgressPercentage])

  if (budgets.length === 0) {
    return (
      <div className={cn('p-8 text-center border border-birhaus-border rounded-lg', className)} {...props}>
        <PieChart size={48} className="mx-auto mb-4 text-birhaus-muted-foreground/50" />
        <h3 className="text-lg font-semibold text-birhaus-foreground mb-2">
          {language === 'es' ? 'No hay presupuestos configurados' : 'No budgets configured'}
        </h3>
        <p className="text-birhaus-muted-foreground mb-4">
          {language === 'es' 
            ? 'Crea tu primer presupuesto para comenzar a hacer seguimiento de tus gastos.'
            : 'Create your first budget to start tracking your expenses.'
          }
        </p>
        {showAddButton && onAddBudget && (
          <button
            onClick={handleAddBudget}
            className="flex items-center gap-2 mx-auto px-4 py-2 bg-birhaus-primary text-white rounded-lg hover:bg-birhaus-primary/90 transition-colors"
          >
            <Plus size={16} />
            <span>{language === 'es' ? 'Crear Presupuesto' : 'Create Budget'}</span>
          </button>
        )}
      </div>
    )
  }

  return (
    <div className={cn('space-y-6', className)} {...props}>
      {/* Header with summary */}
      <div className="bg-gradient-to-r from-birhaus-primary to-birhaus-primary/80 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold mb-1">
              {language === 'es' ? 'Seguimiento de Presupuesto' : 'Budget Tracking'}
            </h2>
            <p className="text-white/80">
              {language === 'es' ? `Período ${getPeriodName(periodo)}` : `${getPeriodName(periodo)} Period`}
            </p>
          </div>
          
          {showAddButton && onAddBudget && (
            <button
              onClick={handleAddBudget}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
              aria-label={language === 'es' ? 'Agregar presupuesto' : 'Add budget'}
            >
              <Plus size={16} />
              <span className="hidden sm:inline">
                {language === 'es' ? 'Agregar' : 'Add'}
              </span>
            </button>
          )}
        </div>

        {/* Total summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Presupuesto Total' : 'Total Budget'}
            </p>
            <p className="text-xl font-bold">
              <BirhausCurrencyDisplay 
                amount={totals.totalBudget} 
                currency={moneda}
                className="text-white"
              />
            </p>
          </div>
          
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Total Gastado' : 'Total Spent'}
            </p>
            <p className="text-xl font-bold">
              <BirhausCurrencyDisplay 
                amount={totals.totalSpent} 
                currency={moneda}
                className="text-white"
              />
            </p>
          </div>
          
          <div>
            <p className="text-white/70 text-sm mb-1">
              {language === 'es' ? 'Restante' : 'Remaining'}
            </p>
            <p className={cn(
              'text-xl font-bold',
              totals.totalRemaining >= 0 ? 'text-green-200' : 'text-red-200'
            )}>
              <BirhausCurrencyDisplay 
                amount={totals.totalRemaining} 
                currency={moneda}
                className={totals.totalRemaining >= 0 ? 'text-green-200' : 'text-red-200'}
              />
            </p>
          </div>
        </div>

        {/* Overall progress bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-white/80 mb-2">
            <span>{language === 'es' ? 'Progreso General' : 'Overall Progress'}</span>
            <span>{totals.spentPercentage.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-500',
                totals.spentPercentage >= 100 
                  ? 'bg-red-300' 
                  : totals.spentPercentage >= 80 
                  ? 'bg-yellow-300'
                  : 'bg-green-300'
              )}
              style={{ width: `${Math.min(100, totals.spentPercentage)}%` }}
            />
          </div>
        </div>
      </div>

      {/* Budget categories */}
      <div className="space-y-4">
        {sortedBudgets.map((budget) => {
          const categoryConfig = budgetCategoryConfig[budget.categoria]
          const budgetStatus = getBudgetStatus(budget)
          const statusConfig = budgetStatusConfig[budgetStatus]
          const progressPercentage = getProgressPercentage(budget)
          const limit = budget.limite ?? budget.montoPresupuesto
          const spent = budget.gastado ?? budget.montoGastado
          const remaining = limit - spent

          return (
            <div
              key={budget.id}
              className={cn(
                'p-4 border rounded-lg transition-all duration-200',
                budgetStatus === 'excedido' 
                  ? 'border-red-200 bg-red-50' 
                  : budgetStatus === 'cerca_limite'
                  ? 'border-yellow-200 bg-yellow-50'
                  : 'border-birhaus-border bg-white hover:shadow-sm'
              )}
            >
              <div className="flex items-start justify-between">
                {/* Category info */}
                <div className="flex items-center gap-4 flex-1">
                  <div className={cn('p-2 rounded-full', categoryConfig.colorClass)}>
                    {categoryConfig.icon}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-birhaus-foreground">
                        {language === 'es' ? categoryConfig.nameEs : categoryConfig.nameEn}
                      </h3>
                      
                      <span className={cn(
                        'flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
                        statusConfig.colorClass
                      )}>
                        {statusConfig.icon}
                        <span>{language === 'es' ? statusConfig.nameEs : statusConfig.nameEn}</span>
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm text-birhaus-muted-foreground mb-1">
                        <span>
                          <BirhausCurrencyDisplay amount={spent} currency={moneda} />
                          {' '}/{' '}
                          <BirhausCurrencyDisplay amount={limit} currency={moneda} />
                        </span>
                        <span>{progressPercentage.toFixed(1)}%</span>
                      </div>
                      
                      <div className="w-full bg-birhaus-muted rounded-full h-2">
                        <div
                          className={cn(
                            'h-2 rounded-full transition-all duration-500',
                            budgetStatus === 'excedido' 
                              ? 'bg-red-500' 
                              : budgetStatus === 'cerca_limite'
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                          )}
                          style={{ width: `${Math.min(100, progressPercentage)}%` }}
                        />
                      </div>
                    </div>

                    {/* Remaining amount */}
                    <p className={cn(
                      'text-sm font-medium',
                      remaining >= 0 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {remaining >= 0 ? (
                        <>
                          {language === 'es' ? 'Restante: ' : 'Remaining: '}
                          <BirhausCurrencyDisplay amount={remaining} currency={moneda} />
                        </>
                      ) : (
                        <>
                          {language === 'es' ? 'Excedido por: ' : 'Over by: '}
                          <BirhausCurrencyDisplay amount={Math.abs(remaining)} currency={moneda} />
                        </>
                      )}
                    </p>

                    {/* Last updated */}
                    {budget.ultimaActualizacion && (
                      <p className="text-xs text-birhaus-muted-foreground mt-2">
                        {language === 'es' ? 'Actualizado: ' : 'Updated: '}
                        {budget.ultimaActualizacion.toLocaleDateString(
                          language === 'es' ? 'es-PY' : 'en-US'
                        )}
                      </p>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                {showEditButtons && (
                  <div className="flex items-center gap-2 ml-4">
                    {onEditBudget && (
                      <button
                        onClick={() => handleEditBudget(budget)}
                        className="p-2 text-birhaus-muted-foreground hover:text-birhaus-foreground hover:bg-birhaus-muted rounded transition-colors"
                        aria-label={language === 'es' ? 'Editar presupuesto' : 'Edit budget'}
                      >
                        <Edit size={16} />
                      </button>
                    )}
                    
                    {onDeleteBudget && (
                      <button
                        onClick={() => handleDeleteBudget(budget)}
                        className="p-2 text-birhaus-muted-foreground hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        aria-label={language === 'es' ? 'Eliminar presupuesto' : 'Delete budget'}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-birhaus-border">
        <div className="text-center">
          <p className="text-2xl font-bold text-green-600">
            {budgets.filter(b => getBudgetStatus(b) === 'en_objetivo').length}
          </p>
          <p className="text-sm text-birhaus-muted-foreground">
            {language === 'es' ? 'En Objetivo' : 'On Track'}
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-600">
            {budgets.filter(b => getBudgetStatus(b) === 'cerca_limite').length}
          </p>
          <p className="text-sm text-birhaus-muted-foreground">
            {language === 'es' ? 'Cerca del Límite' : 'Near Limit'}
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-2xl font-bold text-red-600">
            {budgets.filter(b => getBudgetStatus(b) === 'excedido').length}
          </p>
          <p className="text-sm text-birhaus-muted-foreground">
            {language === 'es' ? 'Excedido' : 'Over Budget'}
          </p>
        </div>
        
        <div className="text-center">
          <p className="text-2xl font-bold text-birhaus-foreground">
            {budgets.length}
          </p>
          <p className="text-sm text-birhaus-muted-foreground">
            {language === 'es' ? 'Total Categorías' : 'Total Categories'}
          </p>
        </div>
      </div>
    </div>
  )
}

export type { BirhausBudgetTrackerProps }